import { useEffect, useState } from "react";
import { throttle } from "lodash";
import type { VideoMetadata } from "../types";

interface UseVideoProgressProps {
  onLoadedMetadata?: (metadata: VideoMetadata) => void;
  onTimeUpdate?: (currentTime: number) => void;
  videoRef: React.RefObject<HTMLMediaElement>;
}

export const useVideoProgress = ({
  videoRef,
  onTimeUpdate,
  onLoadedMetadata,
}: UseVideoProgressProps) => {
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = throttle(() => {
      setCurrentTime(video.currentTime);
      onTimeUpdate?.(video.currentTime);
    }, 500);

    video.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      handleTimeUpdate.cancel();
    };
  }, [onTimeUpdate, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () =>
      onLoadedMetadata?.({ duration: video.duration });

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [onLoadedMetadata, videoRef]);

  return { currentTime };
};
